/* FitPane — fits a visual into its scrolling frame: measures the natural
 * content size + the available pane, scales DOWN to fit (transform only,
 * never below MIN_SCALE — past that the pane scrolls), and offers an
 * Expand toggle back to full size + scroll. Children get the live scale so
 * pointer math (node dragging) can divide deltas correctly. */
import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import {
    Maximize2,
    Minimize2
} from 'lucide-react';
import './visuals.css';

const MIN_SCALE = 0.6;

type Props = {
    /** 'both' fits width+height (graphs); 'x' fits width only (pipelines). */
    axis?: 'both' | 'x';
    children: (scale: number) => React.ReactNode;
};

export default function FitPane({
    axis = 'both',
    children
}: Props) {
    const outerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [fitScale, setFitScale] = useState(1);
    const [size, setSize] = useState<{
        w: number;
        h: number;
    } | null>(null);

    const measure = useCallback(() => {
        const outer = outerRef.current;
        const content = contentRef.current;
        const frame = outer?.parentElement;
        if (!outer || !content || !frame) return;
        const cw = content.scrollWidth;
        const ch = content.scrollHeight;
        if (!cw || !ch) return;
        const aw = Math.max(60, frame.clientWidth - 2);
        const ah = Math.max(60, frame.clientHeight - outer.offsetTop - 2);
        const fit = axis === 'x'
            ? Math.min(1, aw / cw)
            : Math.min(1, aw / cw, ah / ch);
        setFitScale(Math.max(MIN_SCALE, fit));
        setSize({
            w: cw,
            h: ch
        });
    }, [axis]);

    useEffect(() => {
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(measure);
        if (contentRef.current) ro.observe(contentRef.current);
        if (outerRef.current?.parentElement) ro.observe(outerRef.current.parentElement);
        return () => ro.disconnect();
    }, [measure]);

    const scale = expanded ? 1 : fitScale;
    const showToggle = expanded || fitScale < 1;

    return (
        <div
            ref={outerRef}
            className="aps-fitpane"
            style={size ? {
                width: size.w * scale,
                height: size.h * scale
            } : undefined}
        >
            {showToggle && (
                <div className="aps-fitpane-ctl">
                    <button
                        className="aps-icon-btn"
                        title={expanded
                            ? 'Fit to pane (compact)'
                            : 'Expand to full size (scroll)'}
                        onClick={() => setExpanded((v) => !v)}
                    >
                        {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                    </button>
                </div>
            )}
            <div style={{
                transform: `scale(${scale})`,
                transformOrigin: '0 0',
                width: size?.w
            }}>
                <div ref={contentRef} style={{
                    width: 'fit-content'
                }}>
                    {children(scale)}
                </div>
            </div>
        </div>
    );
}
